import type * as ents from '@attain-apps/entities'
import type * as chartJs from 'chart.js'

import * as color from '../../../../../color'
import * as utils from '../utils'
interface VennDataItem {
  sets?: string[]
  value?: number
  label?: string
}

interface DataLabelsContextLike {
  dataIndex?: number
  dataset?: { data?: unknown }
}

/**
 * Transform data from
 * {@link ents.core.display.Chart.Options<ents.core.display.chart.Type.Venn>} to
 * {@link chartJs.ChartOptions<'venn'>}
 */
export function getOptions({
  config,
  data,
}: ents.core.display.chart.Chart<ents.core.display.chart.Type.Venn>): chartJs.ChartOptions<'venn'> {
  let options: chartJs.ChartOptions<'venn'> = {
    ...utils.DEFAULT_OPTIONS,
    layout: {
      padding: 20,
    },
    plugins: {
      legend: {
        display: true,
        labels: {
          boxWidth: 16,
          /**
           * Generate per-set legend items using single-set entries from the dataset.
           */
          generateLabels(chart) {
            const ds = chart.data?.datasets?.[0] as
              | chartJs.ChartDataset<'venn'>
              | undefined
            const dataItems =
              (ds?.data as unknown as
                | { sets?: string[]; label?: string }[]
                | undefined) ?? []
            const bg = (ds?.backgroundColor as string[] | undefined) ?? []
            const baseItems = dataItems
              .map((it, i) => ({ i, it }))
              .filter(({ it }) => (it.sets?.length ?? 0) === 1)

            return baseItems.map(({ i, it }) => ({
              datasetIndex: 0,
              fillStyle: bg[i] ?? '#999',
              hidden: false,
              index: i,
              text: it.label ?? it.sets?.[0] ?? 'Set',
            })) as unknown as chartJs.LegendItem[]
          },
        },
        position: 'top',
      },
      tooltip: {
        callbacks: {
          /**
           * Format the tooltip to show either the intersection label or a
           * joined list of sets with the value and optional unit.
           */
          label(item: chartJs.TooltipItem<'venn'>) {
            const raw = (item.raw as VennDataItem | undefined) ?? {}
            const sets: string[] = raw.sets ?? []
            const isIntersection = (sets?.length ?? 0) > 1
            const label =
              raw.label ??
              (isIntersection ? sets.join(' ∩ ') : (sets?.[0] ?? ''))
            const value = raw.value ?? 0
            const unit = data?.valueLabel ? ` ${data.valueLabel}` : ''
            return `${label}: ${value}${unit}`
          },
        },
      },
    },
  }

  /**
   * Prefer explicit chart config name over data name for title.
   */
  const chartTitle = config?.name ?? data?.name
  if (chartTitle) {
    options = {
      ...options,
      plugins: {
        ...options.plugins,
        title: {
          align: 'center',
          color: color.BLACK,
          display: true,
          font: { weight: 'bold' },
          padding: { bottom: 20 },
          text: chartTitle ?? 'Venn',
        },
      },
    }
  }

  return options
}
